import type React from 'react';

/**
 * Создаёт типобезопасную точку переопределения компонента.
 *
 * В отличие от createSlot (который накапливает несколько обработчиков),
 * createOverride позволяет одному модулю полностью заменить дефолтный компонент.
 *
 * Регистрация происходит в bootstrap.ts клиентского модуля (до рендера приложения).
 *
 * @example
 * // Определение точки переопределения в базовом модуле:
 * export const MyFormOverride = createOverride(DefaultForm);
 *
 * // Использование в компоненте:
 * const Form = MyFormOverride.get();
 * <Form {...props} />
 *
 * // Переопределение в bootstrap.ts клиентского модуля:
 * import { MyFormOverride } from '@modules/base/client/extensions';
 * MyFormOverride.set(CustomForm);
 */
export function createOverride<TProps extends object>(
  defaultComponent: React.ComponentType<TProps>
) {
  let override: React.ComponentType<TProps> | null = null;

  return {
    /**
     * Устанавливает кастомный компонент вместо дефолтного.
     * Вызывается один раз в bootstrap.ts клиентского модуля.
     */
    set(component: React.ComponentType<TProps>): void {
      override = component;
    },

    /**
     * Возвращает активный компонент: кастомный (если зарегистрирован) или дефолтный.
     * Вызывается в React-компоненте для получения актуальной реализации.
     */
    get(): React.ComponentType<TProps> {
      return override ?? defaultComponent;
    },
  };
}
